import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  IEmployeeAppInboxMoment,
  IEmployeeAppInboxResponse,
  IEmployeeAppPinnedItemsResponse,
  IExperienceDenormalized,
  IExperienceDenormalizedStatuses,
  IExperienceDenormalizedV2,
  IMomentDenormalized,
  isSubTaskBlock,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { SsoProviderType } from '@dadohr/shared/domainTypes/sso';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as ENV from '../../../../../env.json';
import { usePromise } from '../../../../../hooks/usePromise';
import {
  LoadingValue,
  LoadingValueLike,
  refreshTimestamp,
  store,
  useCombineLoadingValues,
  useMappedLoadingValue
} from '../../../../../services/firebase';
import { callFirebaseFunction } from '../../../../../services/firebaseFunctions';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../../../services/firecache/documentListener';
import { now } from '../../../../../services/time';
import {
  refreshAndAggregateDenormalizedExperience,
  useStandaloneExperienceStatus
} from '../../../../features/Experience/service';
import { useStandaloneMoment } from '../../../../features/Moment/service';
import {
  EmployeeAppLocalUpdatesForInstance,
  useLocalUpdates
} from './localUpdateProvider';
import { usePinnedItems } from './pinnedItems';

const refreshDenormalizationV2 = (
  d: Doc<IExperienceDenormalizedV2>
): Doc<IExperienceDenormalizedV2> => {
  return {
    ...d,
    data: {
      ...d.data,
      startsAt: refreshTimestamp(d.data.startsAt),
      experienceEnd: d.data.experienceEnd ? {
        at: refreshTimestamp(d.data.experienceEnd.at)
      } : null,
      user: {
        ...d.data.user
      },
      tasks: d.data.tasks.map((t) => ({
        ...t,
        start: refreshTimestamp(t.start),
        end: refreshTimestamp(t.end),
        completedAt: refreshTimestamp(t.completedAt),
        subTasks: t.subTasks.map((s) => {
          if (isSubTaskBlock(s)) {
            s.lastCompletedAt = refreshTimestamp(s.lastCompletedAt);
          }
          if (s.type === 'QUESTION') {
            return {
              ...s,
              data: s.data ? {
                ...s.data,
                answer: s.data.answer
                  ? {
                      ...s.data.answer,
                      createdAt: refreshTimestamp(s.data.answer.createdAt),
                      editedAt: refreshTimestamp(s.data.answer.editedAt)
                    }
                  : null
              } : null
            };
          }
          if (s.type === 'CALENDLY_INVITE' && s.data && s.data.booking) {
            s.data.booking.startTime = refreshTimestamp(
              s.data.booking.startTime
            );
            s.data.booking.endTime = refreshTimestamp(s.data.booking.endTime);
          }
          return s;
        }),
        blocks: t.blocks.map((b) => {
          if (isSubTaskBlock(b)) {
            b.lastCompletedAt = refreshTimestamp(b.lastCompletedAt);
          }
          if (b.type === 'QUESTION') {
            return {
              ...b,
              data: b.data ? {
                ...b.data,
                answer: b.data.answer
                  ? {
                      ...b.data.answer,
                      createdAt: refreshTimestamp(b.data.answer.createdAt),
                      editedAt: refreshTimestamp(b.data.answer.editedAt)
                    }
                  : null
              } : null
            };
          }
          if (b.type === 'CALENDLY_INVITE' && b.data && b.data.booking) {
            b.data.booking.startTime = refreshTimestamp(
              b.data.booking.startTime
            );
            b.data.booking.endTime = refreshTimestamp(b.data.booking.endTime);
          }
          return b;
        }),
        customCompletionCriteria: t.customCompletionCriteria || []
      })),
      messages: d.data.messages.map((m) => {
        // `scheduledSendAt` (the SCHEDULED send time) is a newer denormalized
        // field not yet typed in the published `@dadohr/shared`. We rehydrate it
        // here so downstream sort/pairing can call `.toMillis()`/`.isEqual()`;
        // display still uses `sendAt` (the ACTUAL sent time).
        const scheduledSendAt = (m as { scheduledSendAt?: Timestamp })
          .scheduledSendAt;
        return {
          ...m,
          sendAt: refreshTimestamp(m.sendAt),
          scheduledSendAt: scheduledSendAt
            ? refreshTimestamp(scheduledSendAt)
            : undefined
        };
      }),
      anchor: d.data.anchor
        ? {
            ...d.data.anchor,
            at: refreshTimestamp(d.data.anchor.at)
          }
        : null,
      denormalizedAt: refreshTimestamp(d.data.denormalizedAt)
    }
  };
};

const isLocalhost = () => window.location.host.startsWith('localhost');

const getExperienceDenormalizedV2 = (
  actorAccessToken: string,
  instanceAccessToken: string
) => {
  const url = new URL(
    isLocalhost() ? ENV.domains.cf.public : window.location.origin
  );
  url.pathname = isLocalhost()
    ? 'standaloneApp_v2_getInstance'
    : '/api/v2/getExperience';
  url.searchParams.set('actorAccessToken', actorAccessToken);
  url.searchParams.set('instanceAccessToken', instanceAccessToken);
  url.searchParams.set('currentUrl', window.location.href);
  return fetch(url.toString(), {
    redirect: 'follow',
    headers: {
      Accept: 'application/json'
      // Authorization: authToken ? `Bearer ${authToken}` : ''
    }
  }).then(async (r) => {
    if (
      r.status === 404 &&
      (window.location.origin.startsWith('http://localhost:3115') ||
        window.location.origin.startsWith(
          'https://emplexp-dev-standalone.firebaseapp.com'
        ))
    ) {
      window.location.href = `https://my.dadohr.com${window.location.pathname}`;
    }
    const d:
      | {
          status: 'SSO_REDIRECT';
          location: string;
        }
      | {
          status: 'SSO_WRONG_USER_IN_SESSION';
          sessionEmail: string;
          ssoProvider: SsoProviderType;
        }
      | {
          status: 'OK';
          experience: Doc<IExperienceDenormalizedV2>;
        } = await r.json();
    return new Promise<{
      status: 'OK';
      experience: Doc<IExperienceDenormalizedV2>;
    }>((resolve, reject) => {
      if (d.status === 'SSO_REDIRECT') {
        window.location.href = d.location;
      } else if (d.status === 'SSO_WRONG_USER_IN_SESSION') {
        reject(d);
      } else {
        resolve(d);
      }
    });
  });
};

const DENORM_V2_CACHE: {
  [key: string]: {
    status: 'OK';
    experience: Doc<IExperienceDenormalizedV2>;
  };
} = {};
const toDenormCacheKey = (
  actorInstanceToken: string,
  instanceAccessToken: string,
  v: number,
  inboxUpdatedAtMs: number
) => {
  return [
    actorInstanceToken,
    instanceAccessToken,
    v.toString(),
    inboxUpdatedAtMs.toString()
  ].join('-');
};

export const useExperienceDenormalizedV2 = (
  actorInstanceToken: string,
  instanceAccessToken: string,
  v: number
): LoadingValue<{
  status: 'OK';
  experience: Doc<IExperienceDenormalizedV2>;
}> => {
  // Subscribe to the same Firestore "inbox updated" signal that the inbox
  // listing uses (employeeAppInboxUpdateV1/<accessToken>). The BE writes to
  // this doc whenever an instance the user can see has changed — including
  // when a triggered message is added for a supporting actor (e.g. manager
  // gets a triggered message when the participant completes the prior one).
  //
  // Without this, DENORM_V2_CACHE pins to the first snapshot fetched and
  // never refreshes: inbox listing (separate cache) would show the new
  // triggered message but the detail-page instance fetch returned a stale
  // doc that didn't have it yet, so `instance.data.messages.find()` missed
  // and the user saw "Message not found" until a full page reload reset
  // the module-level cache.
  //
  // Empty-token guard: this hook is invoked from a few sites (incl. the
  // legacy-cache-prime path) before an actor token exists. Firestore's
  // `.doc('')` throws synchronously, so substitute a sentinel id that the
  // BE will never write to — the listener returns no doc → updatedAtMs=0,
  // matching the pre-fix cache key for that pristine state.
  const inboxUpdateToken = actorInstanceToken || '__no_actor__';
  const [inboxUpdateDoc] = useEmployeeAppInboxUpdateDate(inboxUpdateToken);
  const inboxUpdatedAtMs = inboxUpdateDoc?.data.updatedAt.toMillis() ?? 0;
  const key = toDenormCacheKey(
    actorInstanceToken,
    instanceAccessToken,
    v,
    inboxUpdatedAtMs
  );
  const [state, setState] = useState<{
    value:
      | {
          status: 'OK';
          experience: Doc<IExperienceDenormalizedV2>;
        }
      | undefined;
    loading: boolean;
    error: any;
  }>({
    value: DENORM_V2_CACHE[key],
    loading: !DENORM_V2_CACHE[key],
    error: null
  });
  useEffect(() => {
    if (DENORM_V2_CACHE[key]) {
      setState((x) => ({ ...x, value: DENORM_V2_CACHE[key], loading: false }));
      return;
    }
    setState((x) => ({ ...x, loading: true }));
    getExperienceDenormalizedV2(actorInstanceToken, instanceAccessToken).then(
      (res) => {
        DENORM_V2_CACHE[key] = res;
        setState((x) => ({
          ...x,
          value: res,
          loading: false
        }));
      },
      (err) => {
        setState((x) => ({
          ...x,
          error: err,
          loading: false
        }));
      }
    );
  }, [actorInstanceToken, instanceAccessToken, key]);
  return useMemo(() => {
    return [state.value, state.loading, state.error];
  }, [state.value, state.loading, state.error]);
};

// Version-aware wrapper that only applies V2 processing to V2 experiences
const refreshDenormalization = (
  d: Doc<IExperienceDenormalized> | Doc<IExperienceDenormalizedV2>
): Doc<IExperienceDenormalized> | Doc<IExperienceDenormalizedV2> => {
  // If it's V1, return as-is (no V2-specific processing needed)
  if (d.data.v === 1) {
    return d as Doc<IExperienceDenormalized>;
  }
  // If it's V2, apply V2-specific processing
  return refreshDenormalizationV2(d as Doc<IExperienceDenormalizedV2>);
};

export const refreshAndAggregateDenormalizedExperienceV2 = (
  instance: Doc<IExperienceDenormalized> | Doc<IExperienceDenormalizedV2>,
  statusDoc: Doc<IExperienceDenormalizedStatuses> | null,
  localUpdates: EmployeeAppLocalUpdatesForInstance | null
) => {
  const doc = refreshDenormalization(instance);

  // Only apply status doc processing for V2 experiences
  if (
    doc.data.v === 2 &&
    statusDoc &&
    // The actual instance might be younger than these statuses
    // In this case we can rely on the instance data to be fresher and more accurate
    statusDoc.data.createdAt &&
    statusDoc.data.createdAt.toMillis() > (doc.data.denormalizedAt?.toMillis() || 0)
  ) {
    doc.data.status = statusDoc.data.s;
    doc.data.tasks.forEach((t) => {
      const ts = statusDoc.data.ts[t.id];
      t.status = ts?.s || t.status;
      t.subTasks.forEach((s) => {
        const ss = ts?.ss[s.id];
        s.status = ss?.s || s.status;
      });
      t.blocks.forEach((b) => {
        if (isSubTaskBlock(b)) {
          const ss = ts?.ss[b.id];
          b.status = ss?.s || b.status;
          b.lastCompletedAt = refreshTimestamp(b.lastCompletedAt);
        }
      });
    });
    if (statusDoc.data.p) {
      doc.data.progress = statusDoc.data.p;
    }
  }
  doc.data.messages.forEach((m) => {
    const manuallyMarkedAsRead = localUpdates?.msgs[m.templateId]?.markedAsRead
      ? localUpdates.msgs[m.templateId].markedAsRead.read
      : m.markedAsRead?.read;

    // If we have a manually set state, honor it
    // If we don't, see if it's an auto-archived msg, based on it's accompanying tasks
    if (manuallyMarkedAsRead === true) {
      m.archived = true;
    } else if (manuallyMarkedAsRead === false) {
      m.archived = false;
    } else {
      // This is still computed on the fly, as the task statuses are updating on the fly as well
      const accompanyingTasks = doc.data.tasks.filter((t) =>
        t.start && t.start.isEqual && t.start.isEqual(m.sendAt)
      );
      if (
        accompanyingTasks.length &&
        accompanyingTasks.every(
          (t) =>
            t.status === TaskStatus.COMPLETE ||
            t.status === TaskStatus.SKIPPED ||
            t.status === TaskStatus.PAST_DEADLINE
        )
      ) {
        m.archived = true;
      }
    }
  });

  return doc;
};

export const useEmployeeAppExperienceInstance = (
  actorAccessToken: string,
  instanceAccessToken: string,
  v = 0
): LoadingValue<Doc<IExperienceDenormalizedV2>> => {
  const prevView = useRef({ actorAccessToken, instanceAccessToken });
  const isUserUpdate =
    prevView.current.actorAccessToken !== actorAccessToken ||
    prevView.current.instanceAccessToken !== instanceAccessToken;
  prevView.current = { actorAccessToken, instanceAccessToken };
  const { updates } = useLocalUpdates();
  const updatesForInstance = updates[instanceAccessToken] || null;
  const mapFn = useCallback(
    ([res, statusDoc]: [
      { status: 'OK'; experience: Doc<IExperienceDenormalizedV2> },
      Doc<IExperienceDenormalizedStatuses> | null
    ]) => {
      return refreshAndAggregateDenormalizedExperienceV2(
        res.experience,
        statusDoc,
        updatesForInstance
      );
    },
    [updatesForInstance]
  );
  return useMappedLoadingValue(
    useCombineLoadingValues(
      useExperienceDenormalizedV2(actorAccessToken, instanceAccessToken, v),
      useStandaloneExperienceStatus(instanceAccessToken),
      { ignoreIntermediaryLoadingState: !isUserUpdate }
    ),
    mapFn,
    true
  );
};

const getSsoProtected = <X extends { status: 'OK' }>(
  updateUrl: (url: URL) => URL
): Promise<X> => {
  const url = updateUrl(
    new URL(isLocalhost() ? ENV.domains.cf.public : window.location.origin)
  );
  return fetch(url.toString(), {
    redirect: 'follow',
    headers: {
      Accept: 'application/json'
      // Authorization: authToken ? `Bearer ${authToken}` : ''
    }
  }).then(async (r) => {
    if (
      r.status === 404 &&
      (window.location.origin.startsWith('http://localhost:3115') ||
        window.location.origin.startsWith(
          'https://emplexp-dev-standalone.firebaseapp.com'
        ))
    ) {
      window.location.href = `https://my.dadohr.com${window.location.pathname}`;
    }
    const d:
      | {
          status: 'SSO_REDIRECT';
          location: string;
        }
      | {
          status: 'SSO_WRONG_USER_IN_SESSION';
          sessionEmail: string;
          ssoProvider: SsoProviderType;
        }
      | X = await r.json();
    return new Promise<X>((resolve, reject) => {
      if (d.status === 'SSO_REDIRECT') {
        window.location.href = d.location;
      } else if (d.status === 'SSO_WRONG_USER_IN_SESSION') {
        reject(d);
      } else {
        resolve(d);
      }
    });
  });
};

const getDenormalizedExperienceV1OrV2 = (accessToken: string) => {
  return getSsoProtected<{
    status: 'OK';
    experience: Doc<IExperienceDenormalized | IExperienceDenormalizedV2>;
  }>((url) => {
    url.pathname = isLocalhost()
      ? 'standaloneApp_getExperience2'
      : '/api/getExperience';
    url.searchParams.set('accessToken', accessToken);
    url.searchParams.set('currentUrl', window.location.href);
    return url;
  });
};

const refreshInboxData = (d: IEmployeeAppInboxResponse) => {
  d.messages = d.messages || [];
  d.tasks = d.tasks || [];
  d.moments = d.moments || [];

  d.messages.forEach((m) => {
    m.sendAt = refreshTimestamp(m.sendAt);
  });
  d.tasks.forEach((t) => {
    t.timing.start = refreshTimestamp(t.timing.start);
    t.timing.end = refreshTimestamp(t.timing.end);
    t.completedAt = refreshTimestamp(t.completedAt);
  });
  d.moments.forEach((m) => {
    m.sentAt = refreshTimestamp(m.sentAt);
  });
  return d;
};

const EMPLOYEE_INBOX_CACHE: {
  [userAccessToken: string]: {
    promise: Promise<{
      res: {
        status: 'OK';
        data: IEmployeeAppInboxResponse;
      };
    }>;
    lastRetrievalStart: Timestamp;
    lastRetrievedAt: Timestamp | null;
  };
} = {};

export const getEmployeeAppInboxDataBypassingCache = (
  userAccessToken: string
) => {
  return getSsoProtected<{
    status: 'OK';
    data: IEmployeeAppInboxResponse;
  }>((url) => {
    url.pathname = isLocalhost() ? 'standaloneApp_v2_inbox' : '/api/v2/inbox';
    url.searchParams.set('userAccessToken', userAccessToken);
    url.searchParams.set('currentUrl', window.location.href);
    return url;
  }).then((res) => ({
    res,
    updatedAt: now()
  }));
};

export const getEmployeeAppInboxData = (userAccessToken: string) => {
  if (!EMPLOYEE_INBOX_CACHE[userAccessToken]) {
    EMPLOYEE_INBOX_CACHE[userAccessToken] = {
      promise: getEmployeeAppInboxDataBypassingCache(userAccessToken).then(
        (x) => {
          EMPLOYEE_INBOX_CACHE[userAccessToken].lastRetrievedAt = now();
          return x;
        }
      ),
      lastRetrievalStart: now(),
      lastRetrievedAt: null
    };
  }
  return EMPLOYEE_INBOX_CACHE[userAccessToken];
};

// This should put updates into a queue.
// This should be accessible to the rest of the system to figure out
// if there are pending updates or if a new retrieval should be started in the background
// The main hook can then decide when to take the updates into consideration.
// We would only do this either on a full route change. this way especially the batch tasks
// can defer their update until later.
// Alternatively we could spam updates ALL the time, but choose to store the grouping
// results in the group components until there's an actual interest in updating.
// Maybe that's the right way to go.
export const refetchEmployeeAppInboxData = async (userAccessToken: string) => {
  const cached = EMPLOYEE_INBOX_CACHE[userAccessToken];
  if (!cached) {
    // Should never happen!
    return Promise.reject(new Error('Cached data not found'));
  }
  cached.lastRetrievalStart = now();
  const res = await getEmployeeAppInboxDataBypassingCache(userAccessToken);
  cached.promise = Promise.resolve(res);
  cached.lastRetrievedAt = now();
  return res;
};

const DENORM_MULTI_VERSION_CACHE: {
  [key: string]: {
    status: 'OK';
    experience: Doc<IExperienceDenormalized | IExperienceDenormalizedV2>;
  };
} = {};

export const useDenormalizedExperienceV1OrV2 = (
  accessToken: string,
  v: number
): LoadingValue<{
  status: 'OK';
  experience: Doc<IExperienceDenormalized | IExperienceDenormalizedV2>;
}> => {
  const key = [accessToken, v.toString()].join('-');
  const [state, setState] = useState<{
    value:
      | {
          status: 'OK';
          experience: Doc<IExperienceDenormalized | IExperienceDenormalizedV2>;
        }
      | undefined;
    loading: boolean;
    error: any;
  }>({
    value: DENORM_MULTI_VERSION_CACHE[key],
    loading: !DENORM_MULTI_VERSION_CACHE[key],
    error: null
  });

  useEffect(() => {
    setState((x) => ({ ...x, loading: true }));
    getDenormalizedExperienceV1OrV2(accessToken).then(
      (res) => {
        DENORM_MULTI_VERSION_CACHE[key] = res;
        setState((x) => ({
          ...x,
          value: res,
          loading: false
        }));
      },
      (err) => {
        setState((x) => ({
          ...x,
          error: err,
          loading: false
        }));
      }
    );
  }, [accessToken, v, key]);
  return useMemo(() => {
    return [state.value, state.loading, state.error];
  }, [state.value, state.loading, state.error]);
};

export const useEmployeeAppExperienceInstanceV1OrV2 = (
  instanceAccessToken: string,
  v = 0
): LoadingValue<
  Doc<IExperienceDenormalized> | Doc<IExperienceDenormalizedV2>
> => {
  const { updates } = useLocalUpdates();
  const updatesForInstance = updates[instanceAccessToken] || null;
  const mapFn = useCallback(
    ([res, statusDoc]: [
      {
        status: 'OK';
        experience: Doc<IExperienceDenormalized | IExperienceDenormalizedV2>;
      },
      Doc<IExperienceDenormalizedStatuses> | null
    ]) => {
      const instance = res.experience;
      // Why is the union not honored here?
      if (instance.data.v === 2) {
        // This can be removed once we fully transition over to using v2 only
        // Legacy v1-or-v2 path: priming the v2 cache here doesn't have an
        // inbox-update signal to key on, so use 0. The active
        // `useExperienceDenormalizedV2` listener will overwrite with a
        // properly-keyed entry on its next subscribe-and-fetch.
        DENORM_V2_CACHE[toDenormCacheKey('', instanceAccessToken, v, 0)] = {
          status: 'OK',
          experience: instance as Doc<IExperienceDenormalizedV2>
        };
        return refreshAndAggregateDenormalizedExperienceV2(
          instance as Doc<IExperienceDenormalizedV2>,
          statusDoc,
          updatesForInstance
        );
      } else {
        return refreshAndAggregateDenormalizedExperience(
          instance as Doc<IExperienceDenormalized>,
          statusDoc
        );
      }
    },
    [updatesForInstance]
  );
  return useMappedLoadingValue(
    useCombineLoadingValues(
      useDenormalizedExperienceV1OrV2(instanceAccessToken, v),
      useStandaloneExperienceStatus(instanceAccessToken),
      { ignoreIntermediaryLoadingState: true }
    ),
    mapFn,
    true
  );
};

export const useEmployeeAppInboxData = (
  userAccessToken: string
): LoadingValueLike<{
  data: IEmployeeAppInboxResponse;
  lastRetrievedAt: Timestamp | null;
  lastRetrievalStart: Timestamp;
}> => {
  // TODO: might want to combine this with other data, we shall see
  // Same goes for caching. Naive approach first.
  const cached = getEmployeeAppInboxData(userAccessToken);
  return usePromise(async () => {
    const data = await cached.promise;
    return {
      data: refreshInboxData(data.res.data),
      lastRetrievedAt: cached.lastRetrievedAt,
      lastRetrievalStart: cached.lastRetrievalStart
    };
  }, [cached.promise]);
};

export const useEmployeeAppInboxDataAndPinnedItems = (
  userAccessToken: string
): LoadingValue<{
  inbox: IEmployeeAppInboxResponse;
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'];
}> => {
  return useMappedLoadingValue(
    useCombineLoadingValues(
      useEmployeeAppInboxData(userAccessToken),
      usePinnedItems(userAccessToken)
    ),
    ([inbox, pinnedItems]) => {
      return {
        inbox: inbox.data,
        pinnedItems
      };
    }
  );
};

export const useEmployeeAppInboxDataAndPinnedItemsAndUpdateDate = (
  userAccessToken: string
): LoadingValue<{
  inbox: IEmployeeAppInboxResponse;
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'];
  refetchInProgress: boolean;
}> => {
  const [state, setState] = useState<{
    // counter to force a re-render
    v: number;
    refetchInProgress: boolean;
  }>({
    v: 0,
    refetchInProgress: false
  });
  const cached = getEmployeeAppInboxData(userAccessToken);
  const [lUA] = useEmployeeAppInboxUpdateDate(userAccessToken);

  useEffect(() => {
    if (!cached || !lUA) {
      return;
    }
    if (cached.lastRetrievedAt) {
      if (lUA.data.updatedAt.toMillis() > cached.lastRetrievedAt.toMillis()) {
        if (
          cached.lastRetrievalStart.toMillis() < lUA.data.updatedAt.toMillis()
        ) {
          setState((x) => ({ ...x, v: x.v + 1, refetchInProgress: true }));
          console.log('Triggering refetch!');
          refetchEmployeeAppInboxData(userAccessToken).then(() =>
            setState((x) => ({ ...x, v: x.v + 1, refetchInProgress: false }))
          );
        }
      }
    }
  }, [cached, lUA]);

  return useMappedLoadingValue(
    useCombineLoadingValues(
      useEmployeeAppInboxData(userAccessToken),
      usePinnedItems(userAccessToken),
      { ignoreIntermediaryLoadingState: true }
    ),
    ([inbox, pinnedItems]) => {
      return {
        inbox: inbox.data,
        pinnedItems,
        refetchInProgress: state.refetchInProgress
      };
    }
  );
};

const toInboxUpdateDoc = generateToDocFn<{
  updatedAt: Timestamp;
}>();

const inboxUpdateStore = createDocumentListenerGetter(
  (accessToken) =>
    store().collection('employeeAppInboxUpdateV1').doc(accessToken),
  toInboxUpdateDoc
);

export const useEmployeeAppInboxUpdateDate = (userAccessToken: string) => {
  return useDocumentListener(inboxUpdateStore(userAccessToken));
};

export const useEmployeeAppMomentInstance = (
  userAccessToken: string,
  momentInstanceAccessToken: string,
  v = 0
): LoadingValue<Doc<IMomentDenormalized>> => {
  // Best to reimplement this eventually - should also get some caching.
  return useStandaloneMoment(momentInstanceAccessToken, v);
};

const MOMENT_ARCHIVE_CACHE: {
  [userAccessToken: string]: Promise<IEmployeeAppInboxMoment[]>;
} = {};

const getEmployeeAppArchivedMoments = async (userAccessToken: string) => {
  return await callFirebaseFunction<IEmployeeAppInboxMoment[]>(
    'standaloneApp_getArchivedMoments',
    {
      userAccessToken
    }
  );
};

export const useEmployeeAppArchivedMoments = (userAccessToken: string) => {
  return usePromise(() => {
    if (!MOMENT_ARCHIVE_CACHE[userAccessToken]) {
      MOMENT_ARCHIVE_CACHE[userAccessToken] = getEmployeeAppArchivedMoments(
        userAccessToken
      ).then((ms) => {
        ms.forEach((m) => {
          m.sentAt = refreshTimestamp(m.sentAt);
        });
        return ms;
      });
    }
    return MOMENT_ARCHIVE_CACHE[userAccessToken];
  }, [userAccessToken]);
};
